/**
 * Client configuration and endpoint discovery.
 *
 * Lobby URL default is the lobby package's own default port (LOBBY_PORT ?? 8080
 * in packages/lobby/src/main.ts) — kept in sync here so a stock `pnpm dev` boots
 * end to end with no env wiring. All three sources can override, in order of
 * precedence: URL query (?lobby=) > Vite env (VITE_LOBBY_URL) > default.
 */

/** Lobby matchmaking/rooms API base — see packages/lobby/src/main.ts. */
export const DEFAULT_LOBBY_URL = 'http://localhost:8080';

/**
 * Fallback game-server WS URL when a JoinHandoff doesn't carry one. Matches the
 * lobby's GAME_SERVER_URL default (ws://localhost:9000). The real URL normally
 * arrives inside the handoff; this is only a dev fallback.
 */
export const DEFAULT_GAME_WS_URL = 'ws://localhost:9000';

function queryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

export function lobbyUrl(): string {
  return queryParam('lobby') ?? import.meta.env.VITE_LOBBY_URL ?? DEFAULT_LOBBY_URL;
}

export function gameWsFallback(): string {
  return import.meta.env.VITE_GAME_WS_URL ?? DEFAULT_GAME_WS_URL;
}

/**
 * A private-lobby deep link is `.../j/<CODE>` (§9.4). If we were opened on one,
 * surface the code so the menu can pre-fill "join by code".
 */
export function joinCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const m = /\/j\/([A-Za-z0-9]{4,8})/.exec(window.location.pathname);
  if (m) return m[1]!.toUpperCase();
  return queryParam('join');
}
